import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { Upload, Loader2 } from "lucide-react";
import { Form, useRefresh, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormToolbar } from "@/components/admin/simple-form";
import { FileInput } from "@/components/admin/file-input";
import { FileField } from "@/components/admin/file-field";

import { usePapaParse } from "../misc/usePapaParse";
import type { ProspectImportSchema } from "./useProspectImport";
import { useProspectImport } from "./useProspectImport";
import * as sampleCsv from "./prospects_import_sample.csv?raw";

export const ProspectImportButton = () => {
  const translate = useTranslate();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setModalOpen(true)}
        className="flex items-center gap-2 cursor-pointer"
      >
        <Upload />{" "}
        {translate("resources.prospects.import.button", { _: "Import" })}
      </Button>
      <ProspectImportDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

const SAMPLE_URL = `data:text/csv;name=dealengine_prospects_sample.csv;charset=utf-8,${encodeURIComponent(
  sampleCsv.default,
)}`;

function ProspectImportDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose(): void;
}) {
  const translate = useTranslate();
  const refresh = useRefresh();
  const processBatch = useProspectImport();
  const { importer, parseCsv, reset } = usePapaParse<ProspectImportSchema>({
    batchSize: 10,
    processBatch,
  });

  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (importer.state === "complete") refresh();
  }, [importer.state, refresh]);

  const startImport = () => {
    if (file) parseCsv(file);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <Form className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {translate("resources.prospects.import.title", {
                _: "Import prospects",
              })}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col space-y-2">
            {importer.state === "running" && (
              <div className="flex flex-col gap-2">
                <Alert>
                  <AlertDescription className="flex flex-row gap-4">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {translate("resources.prospects.import.running", {
                      _: "Importing…",
                    })}
                  </AlertDescription>
                </Alert>
                <div className="text-sm">
                  {translate("resources.prospects.import.progress", {
                    _: "Imported %{importCount} of %{rowCount} (%{errorCount} errors)",
                    importCount: importer.importCount,
                    rowCount: importer.rowCount,
                    errorCount: importer.errorCount,
                  })}{" "}
                  {importer.remainingTime !== null && (
                    <button
                      onClick={handleReset}
                      className="text-red-600 underline hover:text-red-800"
                    >
                      {translate("resources.prospects.import.stop", {
                        _: "Stop",
                      })}
                    </button>
                  )}
                </div>
              </div>
            )}

            {importer.state === "error" && (
              <Alert variant="destructive">
                <AlertDescription>
                  {translate("resources.prospects.import.error", {
                    _: "Import failed. Check your CSV and try again.",
                  })}
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "complete" && (
              <Alert>
                <AlertDescription>
                  {translate("resources.prospects.import.complete", {
                    _: "Imported %{importCount} prospects (%{errorCount} errors).",
                    importCount: importer.importCount,
                    errorCount: importer.errorCount,
                  })}
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "idle" && (
              <>
                <Alert>
                  <AlertDescription className="flex flex-col gap-4">
                    {translate("resources.prospects.import.sample_hint", {
                      _: "Download the sample CSV to see the expected columns. Rows with a matching parcel APN update the existing prospect.",
                    })}
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to={SAMPLE_URL}
                        download="dealengine_prospects_sample.csv"
                      >
                        {translate("resources.prospects.import.sample_download", {
                          _: "Download sample CSV",
                        })}
                      </Link>
                    </Button>
                  </AlertDescription>
                </Alert>

                <FileInput
                  source="csv"
                  label="resources.prospects.import.csv_file"
                  accept={{ "text/csv": [".csv"] }}
                  onChange={setFile}
                >
                  <FileField source="src" title="title" target="_blank" />
                </FileInput>
              </>
            )}
          </div>
        </Form>

        <div className="flex justify-start pt-6">
          <FormToolbar>
            {importer.state === "idle" ? (
              <Button onClick={startImport} disabled={!file}>
                {translate("resources.prospects.import.button", {
                  _: "Import",
                })}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={importer.state === "running"}
              >
                {translate("ra.action.close")}
              </Button>
            )}
          </FormToolbar>
        </div>
      </DialogContent>
    </Dialog>
  );
}
